/**
 * Push-permission soft-ask orchestrator (R3 Phase 4).
 *
 * One decision function consulted by EVERY surface that soft-asks for push
 * permission — onboarding step, trial-start prompt, daily-practice prompt,
 * insight prompt, No-Spend setup row, and the Profile toggle — so the surfaces
 * stop making independent (and compounding) nag decisions.
 *
 * Design:
 * - `decideSoftAsk()` is PURE: (surface, permission state, history, config,
 *   latch) → { mayShow, reason }. All Firestore/RC access lives in the
 *   `usePushSoftAsk` hook (apps/enough/src/hooks/usePushSoftAsk.ts).
 * - Config comes from the RC JSON param `push_soft_ask_config`, deep-merged
 *   over `DEFAULT_PUSH_SOFT_ASK_CONFIG`. With the param absent, defaults keep
 *   first-show behavior identical to today; the cooldown/cap rules only bite
 *   after a user actually responds (that throttling is the feature).
 * - `push_soft_ask_enabled` stays the master kill-switch for the proactive
 *   asks (`requiresMasterFlag: true`). The insight prompt shipped BEFORE that
 *   flag and has never been gated by it, so its default is `false` — flipping
 *   the master flag on/off cannot regress the already-live insight prompt.
 *   (Set `surfaces.insight.requiresMasterFlag: true` in RC to unify later.)
 * - User-initiated surfaces (Profile toggle, No-Spend setup row) are the user
 *   walking up to US — they bypass cooldowns/cap/latch and never count toward
 *   them, but still record a unified response for analytics.
 * - The unified response history is ADDITIVE (`e_pushSoftAskHistory` on
 *   userContent/{uid}); every surface keeps dual-writing its legacy fields so
 *   nothing downstream breaks. Legacy fields also SEED the history for users
 *   who answered before this shipped (see `historyFromUserDoc`).
 */

import { doc, setDoc, arrayUnion } from 'firebase/firestore';
import { db } from '@shared/services/firebase';
import { logError, logWarn } from '@/lib/log';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SoftAskSurface =
  | 'onboarding'
  | 'trial'
  | 'practice'
  | 'insight'
  | 'no_spend'
  | 'profile'
  | 'home_arriving';

export type SoftAskResponse = 'accepted' | 'declined' | 'dismissed';
export type OsPromptResult = 'granted' | 'denied';

/** One unified soft-ask response record (appended, never rewritten). */
export interface SoftAskRecord {
  surface: SoftAskSurface;
  response: SoftAskResponse;
  osPromptResult?: OsPromptResult;
  /** ISO 8601 instant the response happened. */
  at: string;
}

export interface SoftAskSurfaceConfig {
  /** Per-surface kill switch (RC-overridable). */
  enabled: boolean;
  /** Days before THIS surface may ask again after any recorded response. */
  cooldownDays: number;
  /** Ordering hint for strategies/analytics (lower = earlier in the journey). */
  priority: number;
  /** Whether the `push_soft_ask_enabled` master flag gates this surface. */
  requiresMasterFlag: boolean;
  /**
   * User-initiated surfaces (settings toggle, setup checklist rows) bypass
   * cooldowns, the lifetime cap and the session latch, and their responses
   * never count toward them.
   */
  userInitiated: boolean;
}

export interface PushSoftAskConfig {
  global: {
    /** Days ALL proactive asks pause after any decline/dismiss anywhere. */
    globalDeclineCooldownDays: number;
    /** Max proactive soft-asks ever shown across all surfaces. */
    maxLifetimeAsks: number;
  };
  surfaces: Record<SoftAskSurface, SoftAskSurfaceConfig>;
}

export type SoftAskDecisionReason =
  | 'profile_explicit_intent'
  | 'user_initiated'
  | 'not_native'
  | 'master_disabled'
  | 'surface_disabled'
  | 'already_granted'
  | 'os_denied'
  | 'lifetime_cap'
  | 'global_decline_cooldown'
  | 'surface_cooldown'
  | 'session_latch'
  | 'eligible';

export interface SoftAskDecision {
  mayShow: boolean;
  reason: SoftAskDecisionReason;
}

export interface SoftAskDecisionInput {
  surface: SoftAskSurface;
  isNative: boolean;
  permissionStatus: 'prompt' | 'granted' | 'denied' | 'unknown';
  /** The `push_soft_ask_enabled` RC bool (master kill-switch). */
  masterEnabled: boolean;
  config: PushSoftAskConfig;
  /** Unified soft-ask history (see `historyFromUserDoc`). */
  history: SoftAskRecord[];
  /** Surface that already soft-asked this app session, if any. */
  sessionLatchedSurface: SoftAskSurface | null;
  now?: Date;
}

// ---------------------------------------------------------------------------
// Defaults + RC config parsing
// ---------------------------------------------------------------------------

export const PUSH_SOFT_ASK_SURFACES: SoftAskSurface[] = [
  'onboarding',
  'trial',
  'practice',
  'insight',
  'no_spend',
  'profile',
  'home_arriving',
];

/**
 * Built-in defaults — what runs when the `push_soft_ask_config` RC param is
 * absent. First-show behavior matches today exactly; the cooldowns/cap only
 * apply once responses exist.
 */
export const DEFAULT_PUSH_SOFT_ASK_CONFIG: PushSoftAskConfig = {
  global: { globalDeclineCooldownDays: 7, maxLifetimeAsks: 5 },
  surfaces: {
    onboarding: { enabled: true, cooldownDays: 30, priority: 1, requiresMasterFlag: true, userInitiated: false },
    trial: { enabled: true, cooldownDays: 30, priority: 2, requiresMasterFlag: true, userInitiated: false },
    practice: { enabled: true, cooldownDays: 30, priority: 3, requiresMasterFlag: true, userInitiated: false },
    // Shipped before (and never gated by) push_soft_ask_enabled — see header.
    insight: { enabled: true, cooldownDays: 30, priority: 4, requiresMasterFlag: false, userInitiated: false },
    // Functional setup row inside No-Spend / Shopping Break — user-initiated.
    no_spend: { enabled: true, cooldownDays: 0, priority: 5, requiresMasterFlag: false, userInitiated: true },
    // Explicit user intent — always allowed to proceed (task rule).
    profile: { enabled: true, cooldownDays: 0, priority: 6, requiresMasterFlag: false, userInitiated: true },
    // "Tell me when they are in" on Home's day-one card — the tap is the intent.
    home_arriving: { enabled: true, cooldownDays: 0, priority: 7, requiresMasterFlag: false, userInitiated: true },
  },
};

const isFiniteNonNegative = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0;

const mergeSurface = (
  base: SoftAskSurfaceConfig,
  raw: unknown,
): SoftAskSurfaceConfig => {
  if (!raw || typeof raw !== 'object') return base;
  const o = raw as Record<string, unknown>;
  return {
    enabled: typeof o.enabled === 'boolean' ? o.enabled : base.enabled,
    cooldownDays: isFiniteNonNegative(o.cooldownDays) ? o.cooldownDays : base.cooldownDays,
    priority: typeof o.priority === 'number' && Number.isFinite(o.priority) ? o.priority : base.priority,
    requiresMasterFlag:
      typeof o.requiresMasterFlag === 'boolean' ? o.requiresMasterFlag : base.requiresMasterFlag,
    userInitiated: typeof o.userInitiated === 'boolean' ? o.userInitiated : base.userInitiated,
  };
};

/**
 * Parse the raw `push_soft_ask_config` RC string, deep-merging any provided
 * fields over the defaults. Absent/empty/malformed input ⇒ pure defaults, so
 * behavior with no RC change is unchanged.
 */
export function parsePushSoftAskConfig(raw: string | null | undefined): PushSoftAskConfig {
  const defaults = DEFAULT_PUSH_SOFT_ASK_CONFIG;
  if (!raw) return defaults;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    logWarn('Malformed push_soft_ask_config, using defaults', err);
    return defaults;
  }
  if (!parsed || typeof parsed !== 'object') return defaults;
  const o = parsed as Record<string, unknown>;
  const g = (o.global && typeof o.global === 'object' ? o.global : {}) as Record<string, unknown>;
  const rawSurfaces = (o.surfaces && typeof o.surfaces === 'object' ? o.surfaces : {}) as Record<string, unknown>;

  const surfaces = {} as Record<SoftAskSurface, SoftAskSurfaceConfig>;
  for (const surface of PUSH_SOFT_ASK_SURFACES) {
    surfaces[surface] = mergeSurface(defaults.surfaces[surface], rawSurfaces[surface]);
  }
  return {
    global: {
      globalDeclineCooldownDays: isFiniteNonNegative(g.globalDeclineCooldownDays)
        ? g.globalDeclineCooldownDays
        : defaults.global.globalDeclineCooldownDays,
      maxLifetimeAsks: isFiniteNonNegative(g.maxLifetimeAsks)
        ? g.maxLifetimeAsks
        : defaults.global.maxLifetimeAsks,
    },
    surfaces,
  };
}

// ---------------------------------------------------------------------------
// Session latch — at most ONE proactive soft-ask per app session
// ---------------------------------------------------------------------------

let sessionLatch: SoftAskSurface | null = null;
const latchListeners = new Set<() => void>();

export const getSessionLatch = (): SoftAskSurface | null => sessionLatch;

/** Subscribe to latch changes (useSyncExternalStore-compatible). */
export const subscribeSessionLatch = (listener: () => void): (() => void) => {
  latchListeners.add(listener);
  return () => latchListeners.delete(listener);
};

/**
 * Claim the one-per-session slot for a surface. First claim wins; re-claiming
 * by the same surface is a no-op (a re-render must not hide a visible ask).
 * Returns whether this surface holds the latch.
 */
export const claimSessionLatch = (surface: SoftAskSurface): boolean => {
  if (sessionLatch === null) {
    sessionLatch = surface;
    latchListeners.forEach((l) => l());
  }
  return sessionLatch === surface;
};

/** Test-only reset. */
export const _resetSessionLatchForTests = (): void => {
  sessionLatch = null;
  latchListeners.forEach((l) => l());
};

// ---------------------------------------------------------------------------
// The decision function (pure)
// ---------------------------------------------------------------------------

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const withinDays = (atIso: string, days: number, now: Date): boolean => {
  if (days <= 0) return false;
  const at = Date.parse(atIso);
  if (!Number.isFinite(at)) return false;
  return now.getTime() - at < days * MS_PER_DAY;
};

const latestOf = (records: SoftAskRecord[]): SoftAskRecord | undefined =>
  records.reduce<SoftAskRecord | undefined>(
    (latest, r) => (!latest || r.at > latest.at ? r : latest),
    undefined,
  );

/**
 * Should `surface` show its push soft-ask right now?
 *
 * Rule order (first hit wins — the reason names the blocking rule):
 * profile always proceeds → native only → master flag (where it applies) →
 * per-surface enabled → already granted → user-initiated surfaces proceed →
 * OS-denied → lifetime cap → global decline cooldown → per-surface cooldown →
 * one-per-session latch → eligible.
 */
export function decideSoftAsk(input: SoftAskDecisionInput): SoftAskDecision {
  const { surface, config } = input;
  const now = input.now ?? new Date();
  const cfg = config.surfaces[surface] ?? DEFAULT_PUSH_SOFT_ASK_CONFIG.surfaces[surface];

  // Profile toggle = explicit user intent; it may always proceed (it also
  // owns the deep-link-to-OS-settings path when permission is denied).
  if (surface === 'profile') return { mayShow: true, reason: 'profile_explicit_intent' };

  if (!input.isNative) return { mayShow: false, reason: 'not_native' };
  if (cfg.requiresMasterFlag && !input.masterEnabled) {
    return { mayShow: false, reason: 'master_disabled' };
  }
  if (!cfg.enabled) return { mayShow: false, reason: 'surface_disabled' };
  if (input.permissionStatus === 'granted') return { mayShow: false, reason: 'already_granted' };

  // User-initiated surfaces (No-Spend setup row) bypass the anti-nag rules —
  // including the OS-denied block, since their existing UX deep-links to OS
  // settings from the disclosure modal.
  if (cfg.userInitiated) return { mayShow: true, reason: 'user_initiated' };

  if (input.permissionStatus === 'denied') return { mayShow: false, reason: 'os_denied' };

  // Only proactive surfaces' responses count toward the cap and cooldowns.
  const countable = input.history.filter(
    (r) => !(config.surfaces[r.surface] ?? DEFAULT_PUSH_SOFT_ASK_CONFIG.surfaces[r.surface])?.userInitiated,
  );

  if (countable.length >= config.global.maxLifetimeAsks) {
    return { mayShow: false, reason: 'lifetime_cap' };
  }

  const lastNegative = latestOf(countable.filter((r) => r.response !== 'accepted'));
  if (lastNegative && withinDays(lastNegative.at, config.global.globalDeclineCooldownDays, now)) {
    return { mayShow: false, reason: 'global_decline_cooldown' };
  }

  const lastOnSurface = latestOf(countable.filter((r) => r.surface === surface));
  if (lastOnSurface && withinDays(lastOnSurface.at, cfg.cooldownDays, now)) {
    return { mayShow: false, reason: 'surface_cooldown' };
  }

  if (input.sessionLatchedSurface && input.sessionLatchedSurface !== surface) {
    return { mayShow: false, reason: 'session_latch' };
  }

  return { mayShow: true, reason: 'eligible' };
}

// ---------------------------------------------------------------------------
// Unified response history — read shape + write
// ---------------------------------------------------------------------------

/** Field on userContent/{uid} holding the unified response array. */
export const UNIFIED_HISTORY_FIELD = 'e_pushSoftAskHistory';

const RESPONSES: readonly string[] = ['accepted', 'declined', 'dismissed'];

const isSoftAskRecord = (v: unknown): v is SoftAskRecord => {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.surface === 'string' &&
    (PUSH_SOFT_ASK_SURFACES as string[]).includes(o.surface) &&
    typeof o.response === 'string' &&
    RESPONSES.includes(o.response) &&
    typeof o.at === 'string'
  );
};

/** Sentinel `at` for legacy responses whose timestamp field is missing — old
 *  enough to never trigger a cooldown, but still counted toward the cap. */
const LEGACY_EPOCH = '1970-01-01T00:00:00.000Z';

/**
 * Build the unified history from a userContent doc: the `e_pushSoftAskHistory`
 * array plus SEEDED records from the legacy per-surface fields (onboarding,
 * trial, practice) for users who responded before the orchestrator shipped.
 * A surface already present in the unified array never double-seeds.
 */
export function historyFromUserDoc(data: Record<string, unknown> | undefined): SoftAskRecord[] {
  const unified: SoftAskRecord[] = Array.isArray(data?.[UNIFIED_HISTORY_FIELD])
    ? (data![UNIFIED_HISTORY_FIELD] as unknown[]).filter(isSoftAskRecord)
    : [];
  if (!data) return unified;

  const seeded = new Set(unified.map((r) => r.surface));
  const legacy: SoftAskRecord[] = [];

  const seed = (
    surface: SoftAskSurface,
    responseField: string,
    atField: string,
    osResultField?: string,
  ) => {
    if (seeded.has(surface)) return;
    const response = data[responseField];
    if (typeof response !== 'string' || !RESPONSES.includes(response)) return;
    const at = typeof data[atField] === 'string' ? (data[atField] as string) : LEGACY_EPOCH;
    const osRaw = osResultField ? data[osResultField] : undefined;
    const osPromptResult =
      osRaw === 'granted' || osRaw === 'denied' ? (osRaw as OsPromptResult) : undefined;
    legacy.push({
      surface,
      response: response as SoftAskResponse,
      ...(osPromptResult ? { osPromptResult } : {}),
      at,
    });
  };

  seed('onboarding', 'e_pushSoftAskResponse', 'e_pushSoftAskAt', 'e_pushSystemPromptResult');
  seed('trial', 'e_trialNotifyPromptResponse', 'e_trialNotifyPromptAt');
  seed('practice', 'e_practiceNotifyPromptResponse', 'e_practiceNotifyPromptAt');

  return [...legacy, ...unified].sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
}

/**
 * Append one unified response record to userContent/{uid}. ADDITIVE — callers
 * keep writing their legacy per-surface fields (dual-write) so downstream
 * readers are unaffected. Never throws (recording must not break a surface).
 */
export async function recordUnifiedSoftAskResponse(
  uid: string,
  record: SoftAskRecord,
): Promise<void> {
  try {
    await setDoc(
      doc(db, 'userContent', uid),
      { [UNIFIED_HISTORY_FIELD]: arrayUnion(record) },
      { merge: true },
    );
  } catch (err) {
    logError(err, { context: 'push_soft_ask_record_unified', surface: record.surface });
  }
}
